import React, { useState, useEffect, useCallback } from 'react';
import { 
  LayoutDashboard, Plus, Settings, Trash2, Save, X, GripVertical,
  BarChart3, PieChart, TrendingUp, Target, FileText, AlertTriangle,
  RefreshCw, Edit2, Check, Layers, Brain, Upload, Bell, Sparkles,
  Leaf, Link2, Shield, Briefcase, Database
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import {
  LineChart, Line, BarChart, Bar, PieChart as RechartsPie, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import { apiFetch } from '../services/api_service';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Widget type icons
const WIDGET_ICONS = {
  stat_card: Target,
  line_chart: TrendingUp,
  bar_chart: BarChart3,
  pie_chart: PieChart,
  table: FileText,
  progress: TrendingUp,
  scorecard: Layers,
  recent_activity: Upload,
  alerts: Bell,
  ai_insights: Brain
};

// Category colors
const CATEGORY_COLORS = {
  emissions: 'bg-orange-100 text-orange-700',
  compliance: 'bg-blue-100 text-blue-700',
  evidence: 'bg-green-100 text-green-700',
  data: 'bg-purple-100 text-purple-700',
  alerts: 'bg-red-100 text-red-700',
  targets: 'bg-cyan-100 text-cyan-700',
  ai: 'bg-pink-100 text-pink-700'
};

const CHART_COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#06B6D4'];

// Widget Component
const DashboardWidget = ({ widget, data, onRemove, isEditing }) => {
  const [widgetData, setWidgetData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchWidgetData();
  }, [widget.widget_key]);

  const fetchWidgetData = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${API_URL}/api/custom-dashboards/widget-data/${widget.widget_key}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      const result = await response.json();
      setWidgetData(result.data);
    } catch (error) {
      console.error('Error fetching widget data:', error);
    } finally {
      setLoading(false);
    }
  };

  const Icon = WIDGET_ICONS[widget.type] || Target;

  const renderContent = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center h-32">
          <RefreshCw className="h-6 w-6 animate-spin text-gray-400" />
        </div>
      );
    }

    if (!widgetData) {
      return <p className="text-gray-400 text-center py-8">No data available</p>;
    }

    switch (widget.type) {
      case 'scorecard':
        return (
          <div className="grid grid-cols-4 gap-3">
            {['scope1', 'scope2', 'scope3', 'total'].map((key) => (
              <div key={key} className="text-center p-2 bg-gray-50 rounded-lg">
                <p className="text-xs text-gray-500 capitalize">{key.replace('scope', 'Scope ')}</p>
                <p className="text-lg font-bold">{(widgetData[key] || 0).toLocaleString()}</p>
              </div>
            ))}
          </div>
        );

      case 'progress':
        const progress = widgetData.current_progress || widgetData.verification_rate || 0;
        return (
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span>Progress</span>
              <span className="font-bold">{progress}%</span>
            </div>
            <div className="h-3 bg-gray-200 rounded-full overflow-hidden">
              <div 
                className="h-full bg-blue-600 rounded-full transition-all"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
        );

      case 'line_chart':
        if (!widgetData.series?.[0]?.data) return null;
        return (
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={widgetData.series[0].data}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="year" />
              <YAxis />
              <Tooltip />
              <Line type="monotone" dataKey="value" stroke="#3B82F6" strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        );

      case 'pie_chart':
        if (!widgetData.data) return null;
        return (
          <ResponsiveContainer width="100%" height={200}>
            <RechartsPie>
              <Pie
                data={widgetData.data}
                cx="50%"
                cy="50%"
                innerRadius={40}
                outerRadius={80}
                dataKey="value"
                nameKey="name"
                label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
              >
                {widgetData.data.map((entry, index) => (
                  <Cell key={index} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
            </RechartsPie>
          </ResponsiveContainer>
        );

      case 'stat_card':
        return (
          <div className="grid grid-cols-3 gap-2 text-center">
            <div className="p-2 bg-gray-50 rounded">
              <p className="text-2xl font-bold">{widgetData.total || 0}</p>
              <p className="text-xs text-gray-500">Total</p>
            </div>
            <div className="p-2 bg-green-50 rounded">
              <p className="text-2xl font-bold text-green-600">{widgetData.verified || 0}</p>
              <p className="text-xs text-gray-500">Verified</p>
            </div>
            <div className="p-2 bg-yellow-50 rounded">
              <p className="text-2xl font-bold text-yellow-600">{widgetData.pending || 0}</p>
              <p className="text-xs text-gray-500">Pending</p>
            </div>
          </div>
        );

      case 'recent_activity':
        return (
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {(widgetData.items || []).map((item, idx) => (
              <div key={idx} className="flex items-center gap-2 p-2 bg-gray-50 rounded text-sm">
                <Upload className="h-4 w-4 text-gray-400" />
                <span className="truncate flex-1">{item.file_name}</span>
                <Badge variant="outline" className="text-xs">{item.status}</Badge>
              </div>
            ))}
          </div>
        );

      case 'table':
        return (
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {(widgetData.items || []).slice(0, 5).map((item, idx) => (
              <div key={idx} className="flex items-center justify-between p-2 bg-gray-50 rounded text-sm">
                <span className="truncate">{item.source_file || item.record_id}</span>
                <Badge variant="secondary">Pending</Badge>
              </div>
            ))}
            {widgetData.count > 0 && (
              <p className="text-xs text-gray-400 text-center">{widgetData.count} items</p>
            )}
          </div>
        );

      default:
        return <p className="text-gray-400 text-sm">Widget type: {widget.type}</p>;
    }
  };

  return (
    <Card className="h-full" data-testid={`widget-${widget.widget_id}`}>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {isEditing && <GripVertical className="h-4 w-4 text-gray-400 cursor-move" />}
            <Icon className="h-4 w-4 text-gray-500" />
            <CardTitle className="text-sm">{widget.title}</CardTitle>
          </div>
          {isEditing && (
            <Button variant="ghost" size="sm" onClick={() => onRemove(widget.widget_id)}>
              <X className="h-4 w-4 text-red-500" />
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {renderContent()}
      </CardContent>
    </Card>
  );
};

// Widget Catalog Modal
const WidgetCatalogModal = ({ isOpen, onClose, onAddWidget, catalog }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <Card className="max-w-2xl w-full max-h-[80vh] overflow-auto">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Add Widget</CardTitle>
              <CardDescription>Select a widget to add to your dashboard</CardDescription>
            </div>
            <Button variant="ghost" size="sm" onClick={onClose}>
              <X className="h-5 w-5" />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {catalog?.categories?.map((category) => (
            <div key={category} className="mb-6">
              <h3 className="text-sm font-medium text-gray-500 mb-2 capitalize">{category}</h3>
              <div className="grid grid-cols-2 gap-3">
                {(catalog.by_category[category] || []).map((widget) => {
                  const Icon = WIDGET_ICONS[widget.type] || Target;
                  return (
                    <button
                      key={widget.key}
                      onClick={() => { onAddWidget(widget.key); onClose(); }}
                      className="flex items-start gap-3 p-3 border rounded-lg hover:bg-gray-50 text-left transition-colors"
                    >
                      <div className={`p-2 rounded-lg ${CATEGORY_COLORS[category] || 'bg-gray-100'}`}>
                        <Icon className="h-4 w-4" />
                      </div>
                      <div>
                        <p className="font-medium text-sm">{widget.title}</p>
                        <p className="text-xs text-gray-500">{widget.description}</p>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
};

// Template Card Component
const TemplateCard = ({ template, onSelect }) => {
  const TEMPLATE_ICONS = {
    leaf: Leaf,
    link: Link2,
    shield: Shield,
    briefcase: Briefcase,
    database: Database
  };
  
  const Icon = TEMPLATE_ICONS[template.icon] || LayoutDashboard;
  
  const CATEGORY_COLORS = {
    environmental: 'bg-emerald-100 text-emerald-700 border-emerald-200',
    due_diligence: 'bg-orange-100 text-orange-700 border-orange-200',
    compliance: 'bg-blue-100 text-blue-700 border-blue-200',
    executive: 'bg-purple-100 text-purple-700 border-purple-200',
    data: 'bg-slate-100 text-slate-700 border-slate-200'
  };
  
  return (
    <button
      onClick={() => onSelect(template)}
      className="w-full text-left p-4 border rounded-xl hover:border-blue-400 hover:shadow-md transition-all bg-white group"
      data-testid={`template-${template.template_id}`}
    >
      <div className="flex items-start gap-3">
        <div className={`p-2.5 rounded-lg ${CATEGORY_COLORS[template.category] || 'bg-gray-100'}`}>
          <Icon className="h-5 w-5" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-gray-900 group-hover:text-blue-600 transition-colors">{template.name}</h3>
          <p className="text-sm text-gray-500 mt-0.5 line-clamp-2">{template.description}</p>
          <div className="flex items-center gap-2 mt-2">
            <Badge variant="outline" className="text-xs">
              {template.widget_count} widgets
            </Badge>
            <span className="text-xs text-gray-400 capitalize">{template.category?.replace('_', ' ')}</span>
          </div>
        </div>
      </div>
      <p className="text-xs text-gray-400 mt-3 italic">{template.use_case}</p>
    </button>
  );
};

// Create Dashboard Modal - Enhanced with Templates
const CreateDashboardModal = ({ isOpen, onClose, onCreate, onCreateFromTemplate }) => {
  const [mode, setMode] = useState('templates'); // 'templates' or 'blank'
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isDefault, setIsDefault] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(true);

  useEffect(() => {
    if (isOpen) {
      fetchTemplates();
    }
  }, [isOpen]);

  const fetchTemplates = async () => {
    try {
      const token = localStorage.getItem('token');
      const data = await apiFetch(`/api/custom-dashboards/templates`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setTemplates(data.templates || []);
    } catch (error) {
      console.error('Error fetching templates:', error);
    } finally {
      setLoadingTemplates(false);
    }
  };

  if (!isOpen) return null;

  const handleCreateBlank = () => {
    if (!name.trim()) {
      toast.error('Please enter a dashboard name');
      return;
    }
    onCreate({ name, description, is_default: isDefault });
    setName('');
    setDescription('');
    setIsDefault(false);
    onClose();
  };

  const handleSelectTemplate = (template) => {
    onCreateFromTemplate(template.template_id, isDefault);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <Card className="max-w-2xl w-full max-h-[85vh] overflow-hidden flex flex-col">
        <CardHeader className="border-b">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Create Dashboard</CardTitle>
              <CardDescription>Start from a template or build from scratch</CardDescription>
            </div>
            <Button variant="ghost" size="sm" onClick={onClose}>
              <X className="h-5 w-5" />
            </Button>
          </div>
          {/* Mode Toggle */}
          <div className="flex gap-2 mt-4">
            <button
              onClick={() => setMode('templates')}
              className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-colors ${
                mode === 'templates' 
                  ? 'bg-blue-600 text-white' 
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              <Sparkles className="h-4 w-4 inline mr-2" />
              From Template
            </button>
            <button
              onClick={() => setMode('blank')}
              className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-colors ${
                mode === 'blank' 
                  ? 'bg-blue-600 text-white' 
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              <Plus className="h-4 w-4 inline mr-2" />
              Blank Dashboard
            </button>
          </div>
        </CardHeader>
        
        <CardContent className="flex-1 overflow-y-auto p-4">
          {mode === 'templates' ? (
            <div className="space-y-4">
              {/* Default checkbox for templates */}
              <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                <input
                  type="checkbox"
                  id="isDefaultTemplate"
                  checked={isDefault}
                  onChange={(e) => setIsDefault(e.target.checked)}
                />
                <label htmlFor="isDefaultTemplate" className="text-sm text-gray-600">Set as default dashboard</label>
              </div>
              
              {loadingTemplates ? (
                <div className="flex items-center justify-center py-12">
                  <RefreshCw className="h-6 w-6 animate-spin text-gray-400" />
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {templates.map((template) => (
                    <TemplateCard
                      key={template.template_id}
                      template={template}
                      onSelect={handleSelectTemplate}
                    />
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-4 max-w-md mx-auto py-4">
              <div>
                <label className="text-sm font-medium">Name</label>
                <Input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="My Dashboard"
                  data-testid="dashboard-name-input"
                />
              </div>
              <div>
                <label className="text-sm font-medium">Description</label>
                <Input
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Dashboard description..."
                />
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="isDefault"
                  checked={isDefault}
                  onChange={(e) => setIsDefault(e.target.checked)}
                />
                <label htmlFor="isDefault" className="text-sm">Set as default dashboard</label>
              </div>
              <div className="flex gap-2 pt-2">
                <Button variant="outline" onClick={onClose} className="flex-1">Cancel</Button>
                <Button onClick={handleCreateBlank} className="flex-1" data-testid="create-blank-btn">Create</Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// Main Component
const CustomDashboards = () => {
  const [dashboards, setDashboards] = useState([]);
  const [currentDashboard, setCurrentDashboard] = useState(null);
  const [catalog, setCatalog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [showCatalog, setShowCatalog] = useState(false);
  const [showCreate, setShowCreate] = useState(false);

  useEffect(() => {
    fetchDashboards();
    fetchCatalog();
  }, []);

  const fetchDashboards = async () => {
    try {
      const token = localStorage.getItem('token');
      const data = await apiFetch(`/api/custom-dashboards`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setDashboards(data.dashboards || []);
      
      // Select default or first dashboard
      const defaultDb = data.dashboards?.find(d => d.is_default) || data.dashboards?.[0];
      if (defaultDb) {
        fetchDashboard(defaultDb.dashboard_id);
      }
    } catch (error) {
      console.error('Error fetching dashboards:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchDashboard = async (dashboardId) => {
    try {
      const token = localStorage.getItem('token');
      const data = await apiFetch(`/api/custom-dashboards/${dashboardId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setCurrentDashboard(data);
    } catch (error) {
      console.error('Error fetching dashboard:', error);
    }
  };

  const fetchCatalog = async () => {
    try {
      const token = localStorage.getItem('token');
      const data = await apiFetch(`/api/custom-dashboards/catalog`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setCatalog(data);
    } catch (error) {
      console.error('Error fetching catalog:', error);
    }
  };

  const createDashboard = async (data) => {
    try {
      const token = localStorage.getItem('token');
      const result = await apiFetch(`/api/custom-dashboards`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
      });
      if (result.success) {
        toast.success('Dashboard created');
        fetchDashboards();
        fetchDashboard(result.dashboard.dashboard_id);
      }
    } catch (error) {
      toast.error('Failed to create dashboard');
    }
  };

  const createFromTemplate = async (templateId, isDefault = false) => {
    try {
      const token = localStorage.getItem('token');
      const result = await apiFetch(`/api/custom-dashboards/from-template`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ template_id: templateId, is_default: isDefault })
      });
      if (result.success) {
        toast.success('Dashboard created from template!');
        fetchDashboards();
        fetchDashboard(result.dashboard.dashboard_id);
      } else {
        toast.error(result.detail || 'Failed to create dashboard');
      }
    } catch (error) {
      toast.error('Failed to create dashboard from template');
    }
  };

  const addWidget = async (widgetKey) => {
    if (!currentDashboard) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${API_URL}/api/custom-dashboards/${currentDashboard.dashboard_id}/widgets`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ widget_key: widgetKey })
        }
      );
      const result = await response.json();
      if (result.success) {
        toast.success('Widget added');
        fetchDashboard(currentDashboard.dashboard_id);
      }
    } catch (error) {
      toast.error('Failed to add widget');
    }
  };

  const removeWidget = async (widgetId) => {
    if (!currentDashboard) return;
    
    try {
      const token = localStorage.getItem('token');
      await fetch(
        `${API_URL}/api/custom-dashboards/${currentDashboard.dashboard_id}/widgets/${widgetId}`,
        {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      toast.success('Widget removed');
      fetchDashboard(currentDashboard.dashboard_id);
    } catch (error) {
      toast.error('Failed to remove widget');
    }
  };

  const deleteDashboard = async (dashboardId) => {
    if (!window.confirm('Delete this dashboard?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await apiFetch(`/api/custom-dashboards/${dashboardId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      toast.success('Dashboard deleted');
      setCurrentDashboard(null);
      fetchDashboards();
    } catch (error) {
      toast.error('Failed to delete dashboard');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <RefreshCw className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6" data-testid="custom-dashboards">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <LayoutDashboard className="h-8 w-8 text-blue-600" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Custom Dashboards</h1>
            <p className="text-gray-500">Build your personalized ESG command center</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {currentDashboard && (
            <>
              <Button
                variant={isEditing ? "default" : "outline"}
                onClick={() => setIsEditing(!isEditing)}
              >
                {isEditing ? <Check className="h-4 w-4 mr-1" /> : <Edit2 className="h-4 w-4 mr-1" />}
                {isEditing ? 'Done' : 'Edit'}
              </Button>
              {isEditing && (
                <Button variant="outline" onClick={() => setShowCatalog(true)}>
                  <Plus className="h-4 w-4 mr-1" />
                  Add Widget
                </Button>
              )}
            </>
          )}
          <Button onClick={() => setShowCreate(true)}>
            <Plus className="h-4 w-4 mr-1" />
            New Dashboard
          </Button>
        </div>
      </div>

      {/* Dashboard Tabs */}
      {dashboards.length > 0 ? (
        <Tabs 
          value={currentDashboard?.dashboard_id} 
          onValueChange={(id) => fetchDashboard(id)}
          className="space-y-4"
        >
          <div className="flex items-center gap-4">
            <TabsList>
              {dashboards.map((db) => (
                <TabsTrigger key={db.dashboard_id} value={db.dashboard_id} className="gap-2">
                  {db.name}
                  {db.is_default && <Badge variant="secondary" className="text-xs">Default</Badge>}
                </TabsTrigger>
              ))}
            </TabsList>
            {currentDashboard && isEditing && (
              <Button 
                variant="ghost" 
                size="sm" 
                className="text-red-600"
                onClick={() => deleteDashboard(currentDashboard.dashboard_id)}
              >
                <Trash2 className="h-4 w-4 mr-1" />
                Delete
              </Button>
            )}
          </div>

          {dashboards.map((db) => (
            <TabsContent key={db.dashboard_id} value={db.dashboard_id}>
              {currentDashboard?.widgets?.length > 0 ? (
                <div className="grid grid-cols-12 gap-4">
                  {currentDashboard.widgets.map((widget) => (
                    <div 
                      key={widget.widget_id}
                      className={`col-span-${widget.position?.w || 4}`}
                      style={{ gridColumn: `span ${widget.position?.w || 4}` }}
                    >
                      <DashboardWidget
                        widget={widget}
                        onRemove={removeWidget}
                        isEditing={isEditing}
                      />
                    </div>
                  ))}
                </div>
              ) : (
                <Card>
                  <CardContent className="py-12 text-center">
                    <Layers className="h-12 w-12 mx-auto text-gray-300 mb-4" />
                    <p className="text-gray-500 mb-4">No widgets yet. Add some to get started!</p>
                    <Button onClick={() => { setIsEditing(true); setShowCatalog(true); }}>
                      <Plus className="h-4 w-4 mr-1" />
                      Add Widget
                    </Button>
                  </CardContent>
                </Card>
              )}
            </TabsContent>
          ))}
        </Tabs>
      ) : (
        <Card>
          <CardContent className="py-12 text-center">
            <LayoutDashboard className="h-12 w-12 mx-auto text-gray-300 mb-4" />
            <p className="text-lg font-medium text-gray-700 mb-2">No Dashboards Yet</p>
            <p className="text-gray-500 mb-4">Create your first custom dashboard to get started</p>
            <Button onClick={() => setShowCreate(true)}>
              <Plus className="h-4 w-4 mr-1" />
              Create Dashboard
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Modals */}
      <WidgetCatalogModal
        isOpen={showCatalog}
        onClose={() => setShowCatalog(false)}
        onAddWidget={addWidget}
        catalog={catalog}
      />

      <CreateDashboardModal
        isOpen={showCreate}
        onClose={() => setShowCreate(false)}
        onCreate={createDashboard}
        onCreateFromTemplate={createFromTemplate}
      />
    </div>
  );
};

export default CustomDashboards;
